<template>
    <b-badge :variant="resolvedVariant"
             :pill="pill"
             :class="['status-badge', resolvedClass, customClass]"
             :style="customStyle">
        {{ resolvedLabel }}
    </b-badge>
</template>

<script>
    import { DEFAULT_STATE_COLORS, getStoredStateColors } from './modal-task-columns.vue';

    export default {
        name: 'StatusBadge',
        props: {
            status: {
                type: String,
                default: ''
            },
            variant: {
                type: String,
                default: ''
            },
            pill: {
                type: Boolean,
                default: false
            },
            customClass: {
                type: String,
                default: ''
            },
            customColor: {
                type: String,
                default: ''
            }
        },
        computed: {
            resolvedLabel() {
                return (this.status || '').trim() || '—';
            },
            resolvedCustomColor() {
                if (this.customColor) return this.customColor;
                const state = (this.status || '').trim();
                if (!state) return null;

                // 1. Check custom saved state colors in localStorage
                const storedColors = getStoredStateColors();
                if (storedColors[state]) return storedColors[state];
                const storedEntry = Object.entries(storedColors).find(([k]) => k.toLowerCase() === state.toLowerCase());
                if (storedEntry) return storedEntry[1];
                try {
                    const cached = localStorage.getItem('velocytrack_state_colors');
                    if (cached) {
                        const parsed = JSON.parse(cached);
                        if (parsed && typeof parsed === 'object') {
                            if (parsed[state]) return parsed[state];
                            const entry = Object.entries(parsed).find(([k]) => k.toLowerCase() === state.toLowerCase());
                            if (entry) return entry[1];
                        }
                    }
                } catch (e) {
                    // ignore
                }

                // 2. Exact match in DEFAULT_STATE_COLORS
                if (DEFAULT_STATE_COLORS && DEFAULT_STATE_COLORS[state]) {
                    return DEFAULT_STATE_COLORS[state];
                }

                // 3. Case-insensitive match in DEFAULT_STATE_COLORS
                const normVal = state.toLowerCase();
                if (DEFAULT_STATE_COLORS) {
                    const defEntry = Object.entries(DEFAULT_STATE_COLORS).find(([k]) => k.toLowerCase() === normVal);
                    if (defEntry) {
                        return defEntry[1];
                    }
                }

                // 4. Normalized category fallback for known synonyms
                if (normVal === 'to do' || normVal === 'todo' || normVal === 'proposed' || normVal === 'design' || normVal === 'new') {
                    return storedColors?.['New'] || storedColors?.['To Do'] || DEFAULT_STATE_COLORS?.['To Do'] || '#3b82f6';
                } else if (normVal === 'in progress' || normVal === 'inprogress' || normVal === 'active' || normVal === 'doing' || normVal === 'committed' || normVal === 'started') {
                    return storedColors?.['Active'] || storedColors?.['In Progress'] || DEFAULT_STATE_COLORS?.['In Progress'] || '#10b981';
                } else if (normVal === 'done' || normVal === 'resolved' || normVal === 'closed' || normVal === 'completed') {
                    return storedColors?.['Done'] || storedColors?.['Resolved'] || storedColors?.['Closed'] || DEFAULT_STATE_COLORS?.['Done'] || '#8b5cf6';
                } else if (normVal === 'removed' || normVal === 'cut' || normVal === 'abandoned') {
                    return storedColors?.['Removed'] || DEFAULT_STATE_COLORS?.['Removed'] || '#ef4444';
                }

                return null;
            },
            customStyle() {
                const hex = this.resolvedCustomColor;
                if (!hex) return {};
                return {
                    '--badge-color': hex
                };
            },
            resolvedVariant() {
                if (this.resolvedCustomColor) return null;
                if (this.variant) return this.variant;
                return 'secondary';
            },
            resolvedClass() {
                const normalized = (this.status || '').trim().toLowerCase();
                if (normalized === 'abandoned' && !this.resolvedCustomColor) {
                    return 'status-abandoned';
                }
                return '';
            }
        }
    };
</script>

<style scoped>
    .status-badge {
        font-size: 0.72rem;
        font-weight: 600;
        padding: 0.22rem 0.5rem;
        letter-spacing: 0.02em;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 1.15;
        white-space: nowrap;
        user-select: none;
        vertical-align: middle;
        background-color: #f1f5f9;
        border: 1px solid #cbd5e1;
        color: #0f172a;
    }

    .status-badge[style*="--badge-color"],
    [data-theme="light"] .status-badge[style*="--badge-color"],
    :root:not([data-theme="dark"]) .status-badge[style*="--badge-color"] {
        background-color: color-mix(in srgb, var(--badge-color) 18%, #f1f5f9) !important;
        border-color: color-mix(in srgb, var(--badge-color) 55%, #cbd5e1) !important;
        color: #0f172a !important;
    }

    .status-badge.status-abandoned {
        background-color: #d67730 !important;
        color: #ffffff !important;
    }

    /* Dark Mode Support */
    [data-theme="dark"] .status-badge {
        background-color: #1e293b;
        border-color: #334155;
        color: #f1f5f9;
    }

    [data-theme="dark"] .status-badge.badge-dark {
        background-color: #334155 !important;
        color: #f1f5f9 !important;
        border: 1px solid #475569;
    }

    [data-theme="dark"] .status-badge.badge-light {
        background-color: #1e293b !important;
        color: #e2e8f0 !important;
        border: 1px solid #334155;
    }

    [data-theme="dark"] .status-badge.badge-secondary {
        background-color: #475569 !important;
        color: #f8fafc !important;
    }

    [data-theme="dark"] .status-badge[style*="--badge-color"] {
        background-color: color-mix(in srgb, var(--badge-color) 25%, rgba(15, 23, 42, 0.9)) !important;
        border-color: color-mix(in srgb, var(--badge-color) 60%, transparent) !important;
        color: #ffffff !important;
    }
</style>
